import { GridComponent, Group, Sort, Filter, Toolbar, ExcelExport, Reorder, Resize, ColumnChooser, Inject, FilterSettingsModel, Page, ExcelQueryCellInfoEventArgs, Search } from '@syncfusion/ej2-react-grids';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { GridType } from '../../../../Api/UsersMicroserviceClient';
import InitSfGridLocale from '../../../../Localization/SfGridLanguageInitializer';
import { Autocomplete, Box, Button, Card, Checkbox, TextField, Typography, useTheme } from '@mui/material';
import { useUserLayoutContext } from '../../../../Contexts/Providers/UserLayoutMenuProvider';
import { useApi } from '../../../../ApiOld/Api/useApi';
import { GetWarehousesReportListPageRequest, WarehousesReportListPageDTO, WarehouseVovClient } from '../../../../ApiOld/Api/ApiServerVov';
import GridComponentStatefull from '../../../Shared/Datagrid/GridComponentStatefull';
import WarehouseSelect from '../../../Shared/WarehouseSelect';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Cancel } from '@mui/icons-material';

InitSfGridLocale();
interface WarehousesReportsProps {
}

export default function WarehousesReports(props: WarehousesReportsProps) {
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | undefined>(undefined);
    const [datetimeFrom, setDatetimeFrom] = useState<Date | undefined>(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 3);
        return date;
    });
    const [datetimeTo, setDatetimeTo] = useState<Date | undefined>(new Date());
    const [warehousesReport, setWarehousesReport] = useState<WarehousesReportListPageDTO[] | undefined>();
    const { t, i18n } = useTranslation();
    const [selectedOptions, setSelectedOptions] = useState<string[]>([t('warehouses.purchase'), t('warehouses.receipt'), t('warehouses.reservation'), t('warehouses.disbursement'), t('warehouses.consumption'), t('warehouses.writeOff')]);
    const { setBackMenuFunction, setMenuButtons } = useUserLayoutContext();
    const { authorizedApi } = useApi(WarehouseVovClient);
    const grid = useRef<GridComponent>(null);
    const navigate = useNavigate();
    const params = useParams();
    const options = [t('warehouses.purchase'), t('warehouses.receipt'), t('warehouses.reservation'), t('warehouses.disbursement'), t('warehouses.consumption'), t('warehouses.writeOff')];
    const [autocompleteHeight, setAutocompleteHeight] = useState<number>(48); // default single line height
    const autocompleteRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    let groupOptions = {
        showDropArea: true,
        captionTemplate: '<span class="groupItems" style="color:#FFC600"> ${key} - ${count} Úkolů</span>'
    };

    let filterOptions: FilterSettingsModel = {
        type: 'Excel',
        ignoreAccent: true
    };

    function toolbarClickExcel(props: any) {
        if (grid.current !== undefined && props.item.id.toString().endsWith('excelexport'))
            grid.current!.excelExport();
    }

    const refresh = () => {
        if (selectedOptions.length !== 0) {
            authorizedApi.getWarehousesReportListPage(new GetWarehousesReportListPageRequest({ lang: params['lang'] ?? '', warehouseId: selectedWarehouseId, dateFrom: datetimeFrom, dateTo: datetimeTo, isBuy: selectedOptions.includes(t('warehouses.purchase')), isIn: selectedOptions.includes(t('warehouses.receipt')), isBlock: selectedOptions.includes(t('warehouses.reservation')), isOut: selectedOptions.includes(t('warehouses.disbursement')), isUtility: selectedOptions.includes(t('warehouses.consumption')), isWriteOff: selectedOptions.includes(t('warehouses.writeOff')) })).then((r) => {
                setWarehousesReport(r ? r.warehousesReportListPage : []);

                if (grid.current) {
                    grid.current.dataSource = [];
                    grid.current.refresh(); // Refresh the grid to reflect the empty data source
                }

                // Force a re-render by updating the state
                setTimeout(() => {
                    if (grid.current) {
                        grid.current.dataSource = r ? r.warehousesReportListPage ?? [] : [];
                        grid.current.refresh(); // Refresh the grid again with the new data source
                    }
                }, 0);
            });
        }
        else {
            setWarehousesReport([]);
            setTimeout(() => {
                if (grid.current) {
                    grid.current.dataSource = [];
                    grid.current.refresh();
                }
            }, 0);
        }
    }

    useEffect(() => {
        setBackMenuFunction(undefined);
        setMenuButtons(undefined);
    }, [setBackMenuFunction, setMenuButtons, i18n.language, params, navigate, t]);

    useEffect(() => {
        refresh();
    }, [params, selectedWarehouseId, datetimeFrom, datetimeTo, selectedOptions]);

    useEffect(() => {
        if (autocompleteRef.current) {
            const height = autocompleteRef.current.clientHeight;
            setAutocompleteHeight(height);
        }
    }, [selectedOptions]);

    useEffect(() => {
        if (datetimeFrom && datetimeTo && datetimeFrom > datetimeTo) {
            setDatetimeTo(datetimeFrom);
        }
    }, [datetimeFrom]);

    useEffect(() => {
        if (datetimeFrom && datetimeTo && datetimeFrom > datetimeTo) {
            setDatetimeFrom(datetimeTo);
        }
    }, [datetimeTo]);

    const excelQueryCellInfo = (args: ExcelQueryCellInfoEventArgs): void => {
    }

    function GridTotalSupplyTemplate(props: Readonly<WarehousesReportListPageDTO>) {
        return <Box>{new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.totalUnitsCount ?? 0) + (props.measureUnitAbbr ? ' ' + props.measureUnitAbbr : '')}</Box>
    }

    function GridPriceTemplate(props: Readonly<WarehousesReportListPageDTO>) {
        return <Box>{new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.price ?? 0)} Kč</Box>
    }

    function GridTotalPriceTemplate(props: Readonly<WarehousesReportListPageDTO>) {
        return <Box>{new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.totalPrice ?? 0)} Kč</Box>
    }

    function GridWeightTemplate(props: Readonly<WarehousesReportListPageDTO>) {
        return <Box>{new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.weight ?? 0)} Kg</Box>
    }

    function GridTotalWeightTemplate(props: Readonly<WarehousesReportListPageDTO>) {
        return <Box>{new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.totalWeight ?? 0)} Kg</Box>
    }

    const handleAutocompleteChange = (event: any, newValue: string[]) => {
        setSelectedOptions(newValue);
    };

    const handleSelectAll = () => {
        setSelectedOptions([...options]);
    };

    return <Box sx={{ maxWidth: '100vw' }}>
        <Card sx={{ height: '100%' }} elevation={3}>
            <Box sx={{ marginTop: '10px', marginLeft: '10px', maxWidth: '410px' }}>
                <WarehouseSelect label={t('warehouses.warehouse')} selectedWarehouseId={selectedWarehouseId} onChange={(id) => { setSelectedWarehouseId(id?.id) }} />
                <Box display="flex" gap={2} sx={{ marginTop: '10px' }}>
                    <Typography variant='h6'>{t('warehouses.period')}</Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            inputFormat="DD.MM.YYYY"
                            value={datetimeFrom}
                            onChange={(newValue) => {
                                setDatetimeFrom(newValue ?? undefined);
                            }}
                            renderInput={(params) => <TextField size='small' {...params} style={{ backgroundColor: '#FFFFFF', float: 'left', borderRadius: '20px' }} />}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            inputFormat="DD.MM.YYYY"
                            value={datetimeTo}
                            onChange={(newValue) => {
                                setDatetimeTo(newValue ?? undefined);
                            }}
                            renderInput={(params) => <TextField size='small' {...params} style={{ backgroundColor: '#FFFFFF', float: 'left', borderRadius: '20px' }} />}
                        />
                    </LocalizationProvider>
                </Box>
            </Box>
            <Box width='100%' display='flex' justifyContent='flex-start' ref={autocompleteRef}>
                <Autocomplete
                    multiple
                    sx={{
                        '& .MuiInputBase-root': {
                            maxHeight: 'none' // or any other value you want
                        }
                    }}
                    options={options}
                    value={selectedOptions}
                    onChange={handleAutocompleteChange}
                    disableCloseOnSelect
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            label={t('warehouses.state')}
                            placeholder={t('warehouses.addState')}
                            size='medium'
                            sx={{ backgroundColor: '#FFFFFF', float: 'left', borderRadius: '20px', marginLeft: '10px', width: '405px' }}
                        />
                    )}
                    renderOption={(props, option, state) => (
                        <Box>
                            <li {...props}>
                                <Checkbox
                                    checked={state.selected}
                                    style={{ marginRight: '10px' }}
                                />
                                {option}
                            </li>
                            {option === t('warehouses.writeOff') && <Button
                                variant="contained"
                                onClick={handleSelectAll}
                                sx={{
                                    ml: 2,
                                    mt: 2,
                                    height: '36px',
                                    backgroundColor: theme.palette.primary.dark,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                    }
                                }}
                            >
                                {t('chooseAll')}
                            </Button>}
                        </Box>
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Box
                                {...getTagProps({ index })}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: theme.palette.primary.dark,
                                    color: 'black',
                                    borderRadius: '12px',
                                    padding: '4px 10px',
                                    margin: '2px',
                                    marginTop: '-10px',
                                    fontSize: '0.875rem',
                                    height: '24px',
                                }}
                            >
                                <Typography variant="body2">{option}</Typography>
                                <Cancel
                                    sx={{
                                        marginLeft: '8px',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        color: 'black',
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        const newValue = [...value];
                                        newValue.splice(index, 1);
                                        setSelectedOptions(newValue);
                                    }}
                                />
                            </Box>
                        ))
                    }
                />
            </Box>
            <GridComponentStatefull<WarehousesReportListPageDTO>
                height={`calc(100vh - ${454 + autocompleteHeight}px)`}
                gridType={GridType.WarehousesReport}
                data={warehousesReport ?? []}
                templateColumns={[
                    { field: 'totalUnitsCount', template: GridTotalSupplyTemplate },
                    { field: 'price', template: GridPriceTemplate },
                    { field: 'totalPrice', template: GridTotalPriceTemplate },
                    { field: 'weight', template: GridWeightTemplate },
                    { field: 'totalWeight', template: GridTotalWeightTemplate }
                ]}
                canAdd={true}
                canChangeGroup canToolbarGrid canExcelExport
                id="grid-component-warehouses_list"
                allowSorting={true}
                enablePersistence={false}
                allowResizing={true}
                showColumnChooser={true}
                allowExcelExport={true}
                allowMultiSorting={true}
                allowGrouping={false}
                locale={i18n.language}
                excelQueryCellInfo={excelQueryCellInfo}
                rowHeight={30}
                allowReordering
                filterSettings={filterOptions}
                allowPaging={true}
                style={{ backgroundColor: 'white', overflow: 'auto' }}
                pageSettings={{ pageSize: 20, pageSizes: [10, 20, 30, 50, 100] }}
                toolbarClick={toolbarClickExcel}
                columnDataStateChange={(e) => { console.log(e); }}
                selectionSettings={{ checkboxMode: 'ResetOnRowClick' }}
                allowFiltering={true} groupSettings={groupOptions}
                ref={grid}
                searchLabel={t('warehouses.searchInReport')}
                columnsXX={[
                    { width: '50', minWidth: '50', headerText: '', type: 'checkbox', showInColumnChooser: false },
                    { field: 'id', width: '46', headerText: 'ID', textAlign: 'Right', visible: false, showInColumnChooser: false },
                    { field: 'code', headerText: t('code'), width: '120', minWidth: '100' },
                    { field: 'name', headerText: t('name'), width: '120', minWidth: '100' },
                    { field: 'totalUnitsCount', template: GridTotalSupplyTemplate, headerText: t('warehouses.supply'), width: '120', minWidth: '100' },
                    { field: 'price', template: GridPriceTemplate, headerText: t('warehouses.unitPrice'), width: '120', minWidth: '100' },
                    { field: 'totalPrice', template: GridTotalPriceTemplate, headerText: t('warehouses.totalPrice'), width: '120', minWidth: '100' },
                    { field: 'weight', template: GridWeightTemplate, headerText: t('warehouses.unitWeight'), width: '120', minWidth: '100' },
                    { field: 'totalWeight', template: GridTotalWeightTemplate, headerText: t('warehouses.totalWeight'), width: '120', minWidth: '100' },
                ]}
                moduleName={t('warehouses.warehouses')}
                menuName={t('View')}
            >
                <Inject services={[Page, Sort, Filter, Search, Toolbar, ExcelExport, Reorder, Resize, ColumnChooser, Group]} />
            </GridComponentStatefull>
        </Card>
    </Box >
}
